import React, { forwardRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Calendar } from 'lucide-react';
import styles from './DatePicker.module.scss';

interface DatePickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
    ({ label, error, className, value, onChange, disabled, ...props }, ref) => {
      const [displayValue, setDisplayValue] = useState('');

      // Smart formatting that prevents impossible dates (like 99-99-9999)
      const formatStrictDate = (input: string) => {
        const clean = input.replace(/[^\d*]/g, ''); // Strip non-digits/asterisks

        // If it's the masked state from your API
        if (clean.includes('*')) {
          let res = '';
          for (let i = 0; i < clean.length; i++) {
            // DD-MM-YYYY formatı için tireleri 2. ve 4. karakterden sonra koyuyoruz
            if (i === 2 || i === 4) res += '-';
            res += clean[i];
            if (i >= 7) break;
          }
          return res;
        }

        // Extract parts for validation (DD-MM-YYYY)
        let d = clean.substring(0, 2);
        let m = clean.substring(2, 4);
        let y = clean.substring(4, 8);

        // Validate & format Day
        if (d.length === 1 && parseInt(d) > 3) {
          d = `0${d}`; // Auto-pad (e.g., typing '4' auto-fills '04')
        } else if (d.length === 2) {
          if (parseInt(d) > 31) d = '31'; // Cap at 31
          if (parseInt(d) === 0) d = '01'; // Floor at 01 (prevents 00)
        }

        // Validate & format Month
        if (m.length === 1 && parseInt(m) > 1) {
          m = `0${m}`; // Auto-pad (e.g., typing '5' auto-fills '05')
        } else if (m.length === 2) {
          if (parseInt(m) > 12) m = '12'; // Cap at 12
          if (parseInt(m) === 0) m = '01'; // Floor at 01 (prevents 00)
        }

        // Reconstruct the DD-MM-YYYY string seamlessly
        let res = d;
        if (d.length === 2 && clean.length >= 3) res += '-' + m;
        if (m.length === 2 && clean.length >= 5) res += '-' + y;

        return res;
      };

      // 1. Triggered when the user types on the keyboard
      const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatStrictDate(e.target.value);
        setDisplayValue(formatted);

        if (onChange) {
          // @ts-ignore
          onChange({ ...e, target: { ...e.target, value: formatted, name: props.name || 'birthDate' } });
        }
      };

      // 2. Triggered when the user picks a date from the native calendar UI
      const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value; // Native date picker always returns strict YYYY-MM-DD
        if (!val) return;

        // Takvimden gelen YYYY-MM-DD değerini DD-MM-YYYY formatına çeviriyoruz
        const parts = val.split('-');
        const formatted = `${parts[2]}-${parts[1]}-${parts[0]}`;

        setDisplayValue(formatted);
        if (onChange) {
          // @ts-ignore
          onChange({ ...e, target: { ...e.target, value: formatted, name: props.name || 'birthDate' } });
        }
      };

      useEffect(() => {
        setDisplayValue(formatStrictDate(value ? String(value) : ''));
      }, [value]);

      const isValidCompleteDate = /^\d{2}-\d{2}-\d{4}$/.test(displayValue) && !displayValue.includes('*');

      const nativeDateValue = isValidCompleteDate
          ? `${displayValue.split('-')[2]}-${displayValue.split('-')[1]}-${displayValue.split('-')[0]}`
          : '';

      return (
          <div className={styles.container}>
            {label && <label className={styles.label}>{label}</label>}

            <div className={styles.inputWrapper}>
              {/* Main Input: Text based for free keyboard typing */}
              <input
                  ref={ref}
                  type="text"
                  inputMode="numeric"
                  className={clsx(styles.input, { [styles.hasError]: error }, className)}
                  value={displayValue}
                  onChange={handleTextInput}
                  disabled={disabled}
                  placeholder="GG-AA-YYYY"
                  maxLength={10}
                  {...props}
              />

              {/* Icon Wrapper: Contains the visual icon AND the invisible native picker */}
              <div
                  className={styles.iconWrapper}
                  style={{ pointerEvents: disabled ? 'none' : 'auto' }}
              >
                <Calendar size={18} />

                <input
                    type="date"
                    value={nativeDateValue}
                    onChange={handleNativeDateChange}
                    disabled={disabled}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                />
              </div>
            </div>

            {error && <span className={styles.errorText}>{error}</span>}
          </div>
      );
    }
);

DatePicker.displayName = 'DatePicker';